import { useCallback, useMemo, useContext } from 'react';
import {
  ConversationMetadata,
  ConversationType,
  GroupConversationMetadata,
  MessageType,
  PrivateConversationMetadata,
  TwilioMessageAttributes,
  TwilioUser,
} from 'models/twilio';
import { UserSessionContext } from 'components/UserSession';
import getSentItemNature from '../utils/getSentItemNature';
import useCurrentAttendee from './useCurrentAttendee';

export interface TrackingParams {
  targetType: ConversationType;
  messageType: MessageType;
  targetId: string;
  targetName: string;
  exhibitorId: string;
}

const useMessageTracking = () => {
  const { userSession } = useContext(UserSessionContext);
  const { exhibitorBoothId } = useCurrentAttendee();

  const buildTrackingParams = useCallback((
    metadata: ConversationMetadata,
    otherUser: TwilioUser | null,
    messageAttributes: TwilioMessageAttributes,
  ): TrackingParams => {
    let chatTargetId = null;
    let chatTargetName = null;

    if (metadata.conversationType === ConversationType.PRIVATE) {
      chatTargetId = (metadata as PrivateConversationMetadata).otherUserId;
      chatTargetName = otherUser.name;
    } else if (metadata.conversationType === ConversationType.GROUP) {
      chatTargetId = metadata.uniqueName;
      chatTargetName = (metadata as GroupConversationMetadata).conversationName;
    }

    return {
      targetId: chatTargetId,
      targetType: metadata.conversationType,
      targetName: chatTargetName,
      messageType: messageAttributes.messageType,
      exhibitorId: otherUser?.exhibitorBoothId || null,
    };
  }, []);

  const trackSendAction = useCallback((
    metadata: ConversationMetadata,
    otherUser: TwilioUser | null,
    messageAttributes: TwilioMessageAttributes,
  ) => {
    const {
      targetType,
      messageType,
      targetId,
      targetName,
      exhibitorId,
    } = buildTrackingParams(metadata, otherUser, messageAttributes);

    const senderRole = exhibitorBoothId ? 'Exhibitor' : 'Attendee';

    if (targetType === ConversationType.PRIVATE) {
      if (messageType === MessageType.ContactCard && exhibitorId !== null) {
        window.trackingItemToFirebase(
          userSession, 'Chat', 'NameCard_Send_Exhibitor', 'NameCard_Send_Exhibitor',
          senderRole, exhibitorId, targetName,
        );
      }
      window.trackingItemToFirebase(
        userSession, 'Chat', 'Private_Chatroom', getSentItemNature(messageType),
        senderRole, targetId, targetName,
      );
      return;
    }
    if (targetType === ConversationType.GROUP) {
      window.trackingItemToFirebase(
        userSession, 'Booth', 'Chat',
        getSentItemNature(messageType), senderRole, targetId, targetName,
      );
    }
  }, [userSession, exhibitorBoothId, buildTrackingParams]);

  return useMemo(() => trackSendAction, [trackSendAction]);
};

export default useMessageTracking;
